import React from 'react';
import {
    Button,
    Dialog,
    Grid,
    TextField,
    Typography,
} from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import AppDialogTitle from '../../../ui/appDialogTitle';
import SafeDialogContent from 'components/ui/SafeDialogContent';
import { ValidDataService } from '../../../../service/validDataService';
import { isLocalDev } from '../../../../utils/environment';

const validationSchema = Yup.object({
    brandName: Yup.string().trim().required('Brand name is required'),
});

export default function AddSupplierBrandDialog({
    open,
    onClose,
    onRecordAdded,
    supplier,
    brandType = 'frames',
}) {
    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        if (!supplier?.id) {
            toast.error('Select or create a supplier first');
            setSubmitting(false);
            return;
        }

        try {
            const updateResponse = await ValidDataService.updateValidSupplierRecord(supplier.id, {
                supplierName: supplier.supplierName,
                tenantId: supplier.tenantId || null,
                brands: [{
                    name: values.brandName.trim(),
                    type: brandType,
                    status: 'active',
                }],
            });

            if (updateResponse.code !== 0) {
                toast.error(updateResponse.message || 'Failed to add brand');
                setSubmitting(false);
                return;
            }

            const brandsResponse = await ValidDataService.getBrandsForSupplier(supplier.id);
            const createdBrand = brandsResponse?.data?.find((brand) =>
                brand.name?.trim().toLowerCase() === values.brandName.trim().toLowerCase()
            );

            toast.success(updateResponse.message || 'Brand added');
            resetForm();
            onClose();

            if (createdBrand && onRecordAdded) {
                onRecordAdded(createdBrand);
            }
        } catch (error) {
            if (isLocalDev) {
                console.error(error);
            }
            toast.error('Failed to add brand');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} maxWidth={'sm'} fullWidth>
            <AppDialogTitle
                title={supplier?.supplierName ? `Add Brand for ${supplier.supplierName}` : 'Add Brand'}
                onClose={onClose}
            />
            <SafeDialogContent>
                <Formik
                    initialValues={{ brandName: '' }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ isSubmitting }) => (
                        <Form>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="body2" color="textSecondary">
                                        Add a brand for the selected supplier without leaving the frame workflow.
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Field
                                        as={TextField}
                                        name="brandName"
                                        label="Brand Name"
                                        required
                                        fullWidth
                                        size="small"
                                        autoFocus
                                    />
                                    <ErrorMessage name="brandName">
                                        {(message) => <Typography color={'red'}>{message}</Typography>}
                                    </ErrorMessage>
                                </Grid>
                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={isSubmitting}
                                    >
                                        Add Brand
                                    </Button>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </SafeDialogContent>
        </Dialog>
    );
}